<script setup lang="ts">
  import { computed } from 'vue';
  import { IDestinationSelection } from '@/stores/printing-v2.config';
  import { useV2PrintingUi } from '@/composition/printing/use-printing-v2-ui';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';

  interface IDestinationOption extends IDestinationSelection {
    key: string;
    label: string;
    checked: boolean;
    /** Name of the printer this selection would replace (if any). */
    replaces: string | null;
  }

  const props = withDefaults(
    defineProps<{
      value: IDestinationSelection[];
      /** Printer being edited — its own current assignments never warn. */
      logicalId?: number | null;
      disabled?: boolean;
    }>(),
    { logicalId: null, disabled: false }
  );

  const emit = defineEmits<{
    (e: 'input', value: IDestinationSelection[]): void;
  }>();

  const { v2Store, restaurantStore, logicalById } = useV2PrintingUi();

  function isChecked(destination: string, stationId?: number): boolean {
    return props.value.some(
      (s) => s.destination === destination && s.stationId === stationId
    );
  }

  function replacedBy(
    destination: 'ticket' | 'sticker' | 'comanda',
    stationId?: number
  ): string | null {
    const doc = v2Store.displayConfig;
    if (!doc) return null;
    let currentId: number | null = null;
    if (destination === 'ticket') currentId = doc.assignments.ticket;
    else if (destination === 'sticker') currentId = doc.assignments.sticker;
    else if (stationId != null) {
      currentId = doc.assignments.comandaByStation[stationId] ?? null;
    }
    if (currentId === null || currentId === props.logicalId) return null;
    return logicalById.value.get(currentId)?.name ?? null;
  }

  const options = computed<IDestinationOption[]>(() => {
    const result: IDestinationOption[] = [
      {
        key: 'ticket',
        destination: 'ticket',
        label: '',
        checked: isChecked('ticket'),
        replaces: isChecked('ticket') ? replacedBy('ticket') : null,
      },
    ];
    for (const station of restaurantStore.printStations) {
      result.push({
        key: `comanda:${station.stationId}`,
        destination: 'comanda',
        stationId: station.stationId,
        label: station.name,
        checked: isChecked('comanda', station.stationId),
        replaces: isChecked('comanda', station.stationId)
          ? replacedBy('comanda', station.stationId)
          : null,
      });
    }
    result.push({
      key: 'sticker',
      destination: 'sticker',
      label: '',
      checked: isChecked('sticker'),
      replaces: isChecked('sticker') ? replacedBy('sticker') : null,
    });
    return result;
  });

  function toggle(option: IDestinationOption) {
    if (props.disabled) return;
    if (option.checked) {
      emit(
        'input',
        props.value.filter(
          (s) =>
            !(
              s.destination === option.destination &&
              s.stationId === option.stationId
            )
        )
      );
    } else {
      emit('input', [
        ...props.value,
        { destination: option.destination, stationId: option.stationId },
      ]);
    }
  }
</script>

<template>
  <div class="v2-assignment-picker" :class="{ 'is-disabled': disabled }">
    <div class="picker-chips">
      <button
        v-for="option in options"
        :key="option.key"
        type="button"
        class="picker-chip"
        :class="{ checked: option.checked }"
        :disabled="disabled"
        @click="toggle(option)"
      >
        <PosUiIcon
          :name="option.checked ? 'checkbox-marked' : 'checkbox-blank-outline'"
          class="picker-chip-icon"
        />
        <template v-if="option.destination === 'ticket'">
          {{ $t('printingV2.destTicket') }}
        </template>
        <template v-else-if="option.destination === 'sticker'">
          {{ $t('printingV2.destSticker') }}
        </template>
        <template v-else>{{ option.label }}</template>
      </button>
    </div>

    <div
      v-for="option in options.filter((o) => o.replaces)"
      :key="`warn-${option.key}`"
      class="picker-warning"
    >
      <PosUiIcon name="alert" class="picker-warning-icon" />
      {{ $t('printingV2.replaceWarning', { name: option.replaces }) }}
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-assignment-picker {
    @apply tw-flex tw-flex-col tw-gap-2;

    &.is-disabled {
      @apply tw-opacity-60;
    }

    .picker-chips {
      @apply tw-flex tw-flex-wrap tw-gap-2;

      .picker-chip {
        @apply tw-flex tw-min-h-[40px] tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-neutral-300 tw-bg-white tw-px-3 tw-text-sm tw-font-medium tw-text-neutral-600 tw-transition-colors hover:tw-bg-neutral-50;

        &.checked {
          @apply tw-border-orange-600 tw-bg-orange-50 tw-text-orange-700;
        }

        &:disabled {
          @apply tw-cursor-not-allowed;
        }

        .picker-chip-icon {
          @apply tw-text-lg;
        }
      }
    }

    .picker-warning {
      @apply tw-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-bg-amber-50 tw-px-3 tw-py-2 tw-text-xs tw-text-amber-700;

      .picker-warning-icon {
        @apply tw-text-base;
      }
    }
  }
</style>
